/**
 * Small pure formatters shared by the console pages.
 *
 * Kept intentionally thin: every console page renders currency and
 * token counts identically, so centralising the locale + options here
 * avoids drift.
 */

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');

/**
 * Format a USD cents value (always-positive display). Pass `signed=true` to
 * render charges as "-$0.12" and credits as "+$5.00". Input is integer
 * cents (the wire format since PR-B.5).
 */
export function formatUsdCents(cents: number, signed = false): string {
  if (!Number.isFinite(cents)) {
    return '—';
  }
  const dollars = cents / 100;
  if (!signed) {
    return USD_FORMATTER.format(Math.abs(dollars));
  }
  const base = USD_FORMATTER.format(Math.abs(dollars));
  if (cents > 0) {
    return `+${base}`;
  }
  if (cents < 0) {
    return `-${base}`;
  }
  return base;
}

/**
 * Format decimal USD rates such as model pricing in USD / MTok.
 * These are not cents and must not go through `formatUsdCents`.
 */
export function formatUsdRate(value: number): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return USD_FORMATTER.format(value);
}

/**
 * Render the server-provided decimal-string USD value with the `$` prefix.
 * Server sends strings like `"5.00"` or `"-1.25"`; we pass them through
 * verbatim so trailing zeros survive the JSON round-trip.
 */
export function formatUsdString(raw: string, signed = false): string {
  if (typeof raw !== 'string' || raw.length === 0) {
    return '—';
  }
  const negative = raw.startsWith('-');
  const magnitude = negative ? raw.slice(1) : raw;
  const formatted = `$${magnitude}`;
  if (!signed) return formatted;
  if (negative) return `-${formatted}`;
  if (magnitude === '0.00') return formatted;
  return `+${formatted}`;
}

export function formatTokens(count: number): string {
  if (!Number.isFinite(count) || count < 0) {
    return '—';
  }
  return TOKEN_FORMATTER.format(Math.floor(count));
}

/**
 * Render an ISO-8601 timestamp as "YYYY-MM-DD HH:mm UTC". Falls back
 * to the raw string on parse failure so we never print "Invalid Date".
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.length === 0) {
    return '—';
  }
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) {
    return iso;
  }
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  );
}

/**
 * Back-compat alias for `formatUsdCents`. Keeps call sites that still
 * reference `formatUsd` compiling after PR-B.5's cents migration. New code
 * should prefer `formatUsdCents` or `formatUsdString` directly.
 */
export const formatUsd = formatUsdCents;
